import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useMemo, useRef, useState } from 'react';
import { useSearchParamsUtils } from 'app/hooks/useSearchParamsUtils';
import map from 'lodash-es/map';
import { useNavigate } from 'react-router-dom';
import ListSkeleton from 'shared/lib/components/plugs/listSkeleton';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import {
  ContentFileItem,
  ContentFolderItem,
  ContentItem,
  ContentSectionItem,
  ContentTableItem,
  ExplorerEntityTypes,
  SortContentKey,
  SortContentOrder,
} from 'entities/types';
import ContentGridContainer from 'shared/lib/extendedComponents/grids/content/contentGridContainer';
import ContentFolderRow from 'shared/lib/extendedComponents/grids/content/contentFolderRow';
import ContentFileRow from 'shared/lib/extendedComponents/grids/content/contentFileRow';
import ContentTableRow from 'shared/lib/extendedComponents/grids/content/contentTableRow';
import DesktopContentHeaderRow from 'shared/lib/extendedComponents/grids/content/contentHeaderRow/desktopContentHeaderRow';
import MobileContentHeaderRow from 'shared/lib/extendedComponents/grids/content/contentHeaderRow/mobileContentHeaderRow';
import RestoreFileModalController, {
  RestoreFileeModalActions,
} from 'widgets/files/restoreFileModalController';
import ContentSectionRow from 'shared/lib/extendedComponents/grids/content/contentSectionRow';
import FinallyDeleteFileModalController, {
  FinallyDeleteFileModalActions,
} from 'widgets/files/finallyDeleteFileModalController';
import FinallyDeleteFolderModalController, {
  FinallyDeleteFolderAfterReactions,
} from 'widgets/folders/finallyDeleteFolderModalController';
import RestoreFolderModalController, {
  RestoreFolderAfterReactions,
} from 'widgets/folders/restoreFolderModalController';
import FinallyDeleteSectionModalController, {
  FinallyDeleteSectionModalControllerActions,
} from 'widgets/sections/finallyDeleteSectionModalController';
import RestoreSectionModalController, {
  RestoreSectionModalControllerActions,
} from 'widgets/sections/restoreSectionModalController';
import FinallyDeleteTableModalController, {
  FinallyDeleteTableModalActions,
} from 'widgets/tables/finallyDeleteTableModalController';
import RestoreTableModalController, {
  RestoreTableModalActions,
} from 'widgets/tables/restoreTableModalController';

export default observer(function TrashContentGridController({
  items,
  sort,
  onSortChange,
  navigatePathname = '/trash_explorer',
  supportTypes = [
    ExplorerEntityTypes.FILE,
    ExplorerEntityTypes.FOLDER,
    ExplorerEntityTypes.TABLE,
  ],
  afterFolderRestore,
  afterFolderFinallyDelete,
  afterFileRestore,
  afterTableRestore,
  afterFileDelete,
  afterSectionRestore,
  afterSectionFinallyDelete,
  afterTableDelete,
}: {
  items: ContentItem[] | null;
  navigatePathname?: string;
  onSortChange?: (params: {
    key: SortContentKey;
    order: SortContentOrder;
  }) => void;
  sort?: { key: SortContentKey; order: SortContentOrder };
  supportTypes?: ExplorerEntityTypes[];
} & RestoreFolderAfterReactions &
  FinallyDeleteFolderAfterReactions &
  RestoreFileeModalActions &
  RestoreTableModalActions &
  FinallyDeleteFileModalActions &
  FinallyDeleteSectionModalControllerActions &
  RestoreSectionModalControllerActions &
  FinallyDeleteTableModalActions) {
  const navigate = useNavigate();

  const [restoringFolder, setRestoringFolder] =
    useState<ContentFolderItem | null>(null);
  const [deletingFolder, setDeletingFolder] =
    useState<ContentFolderItem | null>(null);
  const [restoringFile, setRestoringFile] = useState<ContentFileItem | null>(
    null
  );
  const [restoringTable, setRestoringTable] = useState<ContentTableItem | null>(
    null
  );
  const [deletingFile, setDeletingFile] = useState<ContentFileItem | null>(
    null
  );
  const [deletingTable, setDeletingTable] = useState<ContentTableItem | null>(
    null
  );
  const [deletingSection, setDeletingSection] =
    useState<ContentSectionItem | null>(null);

  const [restoringSection, setRestoringSection] =
    useState<ContentSectionItem | null>(null);

  const { getFolderSearchParams, getSectionSearchParams } =
    useSearchParamsUtils();

  const navigateToSection = useRef(({ sectionId }: { sectionId: string }) => {
    navigate({
      pathname: navigatePathname,
      search: getSectionSearchParams({
        sectionId,
        in_trash: true,
      }),
    });
  });

  // TODO: Реализация навигации в файлы и таблицы

  const navigateToFolder = useRef(({ folderId }: { folderId: string }) => {
    navigate({
      pathname: navigatePathname,
      search: getFolderSearchParams({
        folderId,
        in_trash: true,
      }),
    });
  });

  const { isLessLaptop } = useBooleanScreens();

  const _onSortChange = useRef(onSortChange);
  _onSortChange.current = onSortChange;
  const _supportTypes = useRef(supportTypes);
  _supportTypes.current = supportTypes;

  const { key, order } = sort || {};
  const memoSort = useMemo(() => {
    if (key && order) {
      return {
        key,
        order,
      };
    }

    return;
  }, [key, order]);

  const grid = useMemo(
    () => (
      <ContentGridContainer supportTypes={_supportTypes.current}>
        {isLessLaptop ? (
          <MobileContentHeaderRow
            value={memoSort}
            onChange={_onSortChange.current}
            supportTypes={_supportTypes.current}
          ></MobileContentHeaderRow>
        ) : (
          <DesktopContentHeaderRow
            supportTypes={_supportTypes.current}
            value={memoSort}
            onChange={_onSortChange.current}
          ></DesktopContentHeaderRow>
        )}
        {map(items, (item) => {
          if (item.type === ExplorerEntityTypes.SECTION) {
            return (
              <ContentSectionRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onClick={() => {
                  navigateToSection.current({
                    sectionId: item.id,
                  });
                }}
                onRestore={() => {
                  setRestoringSection(item);
                }}
                onDelete={() => {
                  setDeletingSection(item);
                }}
              ></ContentSectionRow>
            );
          }
          if (item.type === ExplorerEntityTypes.FOLDER) {
            return (
              <ContentFolderRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onClick={() => {
                  navigateToFolder.current({
                    folderId: item.id,
                  });
                }}
                onRestore={() => {
                  setRestoringFolder(item);
                }}
                onDelete={() => {
                  setDeletingFolder(item);
                }}
              ></ContentFolderRow>
            );
          }
          if (item.type === ExplorerEntityTypes.FILE) {
            return (
              <ContentFileRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onRestore={() => {
                  setRestoringFile(item);
                }}
                onDelete={() => {
                  setDeletingFile(item);
                }}
              ></ContentFileRow>
            );
          }
          if (item.type === ExplorerEntityTypes.TABLE) {
            return (
              <ContentTableRow
                key={item.id}
                data={item}
                supportTypes={_supportTypes.current}
                onRestore={() => {
                  setRestoringTable(item);
                }}
                onDelete={() => {
                  setDeletingTable(item);
                }}
              ></ContentTableRow>
            );
          }
        })}
      </ContentGridContainer>
    ),
    [isLessLaptop, items, memoSort]
  );

  return (
    <>
      {items !== null ? (
        items.length ? (
          grid
        ) : (
          <Typography sx={{ fontWeight: 'bold' }} variant="f20">
            Здесь ничего нет
          </Typography>
        )
      ) : (
        <ListSkeleton></ListSkeleton>
      )}

      {restoringFolder && (
        <RestoreFolderModalController
          folder={restoringFolder}
          handleClose={() => setRestoringFolder(null)}
          afterFolderRestore={afterFolderRestore}
        ></RestoreFolderModalController>
      )}

      {deletingFolder && (
        <FinallyDeleteFolderModalController
          id={deletingFolder.id}
          handleClose={() => setDeletingFolder(null)}
          afterFolderFinallyDelete={afterFolderFinallyDelete}
        ></FinallyDeleteFolderModalController>
      )}

      {restoringFile && (
        <RestoreFileModalController
          file={restoringFile}
          handleClose={() => setRestoringFile(null)}
          afterFileRestore={afterFileRestore}
        ></RestoreFileModalController>
      )}

      {restoringTable && (
        <RestoreTableModalController
          table={restoringTable}
          handleClose={() => setRestoringTable(null)}
          afterTableRestore={afterTableRestore}
        ></RestoreTableModalController>
      )}

      {deletingFile && (
        <FinallyDeleteFileModalController
          id={deletingFile.id}
          handleClose={() => setDeletingFile(null)}
          afterFileDelete={afterFileDelete}
        ></FinallyDeleteFileModalController>
      )}

      {deletingTable && (
        <FinallyDeleteTableModalController
          id={deletingTable.id}
          handleClose={() => setDeletingTable(null)}
          afterTableDelete={afterTableDelete}
        ></FinallyDeleteTableModalController>
      )}

      {restoringSection && (
        <RestoreSectionModalController
          section={restoringSection}
          handleClose={() => setRestoringSection(null)}
          afterSectionRestore={afterSectionRestore}
        ></RestoreSectionModalController>
      )}

      {deletingSection && (
        <FinallyDeleteSectionModalController
          id={deletingSection.id}
          handleClose={() => setDeletingSection(null)}
          afterSectionFinallyDelete={afterSectionFinallyDelete}
        ></FinallyDeleteSectionModalController>
      )}
    </>
  );
});
